import React, { useEffect, useState, useRef, memo } from "react";
import apiRequest from "../../services/apiClient";
import { formatDate } from "../../utils/utils";

const ClientDetail = memo(({ selectedItem, handleUnselectItem, handleEditItem, handleDeleteItem }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const currentRequestRef = useRef(null);

  useEffect(() => {
    console.log('ClientDetail useEffect triggered with selectedItem:', selectedItem);
    if (selectedItem && !loadingRef.current) {
      console.log('Making API request for client:', selectedItem);
      loadingRef.current = true;
      setLoading(true);
      
      // Cancelar petición anterior si existe
      if (currentRequestRef.current) {
        console.log('Cancelling previous request');
        currentRequestRef.current.cancel?.();
      }
      
      const request = apiRequest(`client/${selectedItem}?$expand=userCreator($select=fullName),userModifier($select=fullName)`);
      currentRequestRef.current = request;
      
      request
        .then(response => {
          // Solo actualizar si esta es la petición más reciente
          if (currentRequestRef.current === request) {
            console.log('API response received for client:', selectedItem, response.data);
            setClient(response.data);
            setLoading(false);
            loadingRef.current = false;
            currentRequestRef.current = null;
          } else {
            console.log('Ignoring stale response for client:', selectedItem);
          }
        })
        .catch(error => {
          // Solo manejar error si esta es la petición más reciente
          if (currentRequestRef.current === request) {
            console.error('Error loading client:', error);
            setLoading(false);
            loadingRef.current = false;
            currentRequestRef.current = null;
          } else {
            console.log('Ignoring stale error for client:', selectedItem);
          }
        });
    }
  }, [selectedItem]);

  // Limpiar estado cuando se deselecciona
  useEffect(() => {
    if (!selectedItem) {
      setClient(null);
      setLoading(false);
      loadingRef.current = false;
      // Cancelar petición pendiente
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel?.();
        currentRequestRef.current = null;
      }
    }
  }, [selectedItem]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel?.();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <p className="text-gray-500">No client selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
            <input
              type="text"
              value={client.id || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={client.name || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identity Number</label>
            <input
              type="text"
              value={client.identityNumber || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <input
              type="text"
              value={formatDate(client.created) || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Updated</label>
            <input
              type="text"
              value={formatDate(client.updated) || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          {client.userCreator && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Creator</label>
              <input
                type="text"
                value={client.userCreator?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          {client.userModifier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Modifier</label>
              <input
                type="text"
                value={client.userModifier?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}
        </div>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={handleUnselectItem}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleEditItem}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteItem}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

ClientDetail.displayName = 'ClientDetail';

export default ClientDetail;